import { useEffect, useState, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Table, Form, Pagination, Dropdown, InputGroup, Button } from "react-bootstrap";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getBondsDataService } from "../../services/BondsServices";
import moment from "moment/moment";
import {
  exportTableDataAsCSV,
  exportTableAsPDF,
} from "../../utils/systemUtils";
import {
  faFileDownload,
  faSort,
  faSearch,
  faPrint,
  faPlusCircle,
  faClock,
  faTimesCircle,
  faEye,
  faCheckCircle,
  faShieldAlt,
  faFolder,
  faNoteSticky,
  faHourglassHalf
} from "@fortawesome/free-solid-svg-icons";
import { Settings } from "react-feather";
import { BOND_STATUS_LABEL } from "../../types";
import { useOktaAuth } from "@okta/okta-react";

const BondCounselUserLandingPage = () => {
  const { oktaAuth, authState } = useOktaAuth();
  const bondsDaTableRef = useRef(null);
  const fullHiddenBondsDaTableRef = useRef(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const user = useSelector((state) => state.user);
  const [bondsData, setBondsData] = useState([]);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [sortDirection, setSortDirection] = useState("ascending");
  const [currentData, setCurrentData] = useState([]);
  const [filterBy, setFilterBy] = useState("all")
  const searchEnableRef = useRef(false);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const USDollar = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        let bonds_respo = await getBondsDataService(user.userinfo.email);
        bonds_respo = bonds_respo.map((bond) => {
        let formatted_date = ''
        if (bond.closing_date) {
          let parsedDate = moment(bond.closing_date, ['DD-MM-YYYY', moment.ISO_8601, 'MMMM D YYYY'], true);
          if (!parsedDate.isValid()) {
            parsedDate = moment(new Date(bond.closing_date))
          }
          if (parsedDate.isValid()) {
            formatted_date = parsedDate.format("MM/DD/YYYY")
          }

        }

        return {
          ...bond,
          closing_date: formatted_date
        }

      })

        setBondsData(bonds_respo);
        setError("");
      } catch (error) {
        setError("Error fetching Bonds");
      }
    };
    fetchData();
  }, [user]);

  useEffect(() => {
    if (authState?.isAuthenticated) {
      setIsLoggedIn(true);
    }
    if (!isLoggedIn) {
      return navigate("/");
    }
  }, [authState]);

  useEffect(() => {
    if (!isLoggedIn) {
      return navigate("/");
    }
  }, [isLoggedIn]);

  useEffect(() => {
    const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    setCurrentData(currentItems);
  }, [filteredData, currentPage]);

  useEffect(() => {
    if (!bondsData || bondsData.length === 0) return;

    // Reset when search is cleared
    if (!searchTerm || searchTerm.trim().length === 0) {
      setFilteredData(bondsData);
      onChangeFilter(filterBy);
      searchEnableRef.current = false;
      return;
    }

    const term = searchTerm.toLowerCase().trim();

    const filtered = filteredData.filter((bond) => {
      // Safe string helper
      const safe = (val) =>
        val ? val.toString().toLowerCase() : '';

      const lawFirm = safe(bond.law_firm);
      const aagName = safe(bond.aag_name);
      const issuer = safe(bond.Issuer);
      const title = safe(bond.title);
      const bondNumber = safe(bond.bond_issue_number);
      const bondStatus = safe(bond.bond_status);
      const parDollar = safe(bond.par_dollar_amount);
      const opinionRequestedBy = safe(bond.opinion_requested_by);

      // ✅ UPDATED: use raw string instead of normalized date
      const closingDate = safe(bond.closing_date);
      // Search inside nested array safely
      const matchAagUser =
        Array.isArray(bond.aag_userinfo) &&
        bond.aag_userinfo.some((person) =>
          safe(person?.FirstName).includes(term)
        );

      // TEXT MATCH
      const textMatch =
        lawFirm.includes(term) ||
        aagName.includes(term) ||
        issuer.includes(term) ||
        title.includes(term) ||
        bondNumber.includes(term) ||
        bondStatus.includes(term) ||
        parDollar.includes(term) ||
        opinionRequestedBy.includes(term);

      // ✅ UPDATED: simple string match for date
      const dateMatch =
        closingDate.includes(term);

      return textMatch || dateMatch || matchAagUser;
    });

    setFilteredData(filtered);

    // Optional: trigger filter state once
    // if (!searchEnableRef.current) {
    //   onChangeFilter('my_transcripts');
    //   searchEnableRef.current = true;
    // }
  }, [bondsData, searchTerm, filterBy]);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleSorting = (key) => {
    const sortedItems = [...filteredData].sort((a, b) => {
      let itemA = a[key];
      let itemB = b[key];

      if (key === "par_dollar_amount") {
        itemA = parseFloat(itemA);
        itemB = parseFloat(itemB);
      } 
      else if (key === 'closing_date') {
        const parseDate = (dateStr) => {
          if (!dateStr) return 0;
          const [month, day, year] = dateStr.split('/');
          return new Date(year, month - 1, day).getTime();
        };

        itemA = parseDate(itemA);
        itemB = parseDate(itemB);
      }
      else {
        itemA = itemA?.toString().toUpperCase();
        itemB = itemB?.toString().toUpperCase();
      }

      if (itemA < itemB) {
        return sortDirection === "ascending" ? -1 : 1;
      }
      if (itemA > itemB) {
        return sortDirection === "ascending" ? 1 : -1;
      }
      return 0;
    });

    setFilteredData(sortedItems);
    setSortDirection(
      sortDirection === "ascending" ? "descending" : "ascending"
    );
  };

  const onChangeFilter = async (target) => {
    setFilterBy(target)
    const user_email = user.userinfo.email;
    setCurrentPage(1);
    let temp = [...bondsData];

    if (target === "my_transcripts") {
      // if(!Array.isArray(bondsData)){
      //   console.error("bondsData is not an array!", bondsData)
      // }  
      temp = bondsData.filter(bond => (bond.paralegal_userinfo ||
        []).some(user => user.EmailID.toLowerCase() === user_email.toLowerCase()) ||
        (bond.bondcounsel_userinfo || []).some(user => user.EmailID.toLowerCase() === user_email.toLowerCase()))
      // let combinedEmails;
      //  bondsData.filter((bond) => {
      //   console.log(bond,'reponse bond counsel')
      //    // Get the array of CSL emails, lowercased
      //    const bond_counselEmails = bond.bondcounsel_userinfo?.map(
      //      (user) => user.EmailID?.toLowerCase()
      //    ) || [];

      //    // Get the array of legal assistant emails, lowercased
      //    const assistantEmails = bond.paralegal_userinfo?.map(
      //      (user) => user.EmailID?.toLowerCase()
      //    ) || [];
      //    combinedEmails = [...bond_counselEmails, ...assistantEmails]
      //  //  console.log("assistantEmails", assistantEmails)
      //  //  console.log("bond_counselEmails", bond_counselEmails)
      //    console.log("user_email", user_email,[...bond_counselEmails, ...assistantEmails])
      //    // Combine them and check if they include the user email (case-insensitive)
      //    return [...bond_counselEmails, ...assistantEmails].some(user_email?.toLowerCase());
      //  });

      //  temp = combinedEmails


    }
    if (target === "unassigned") {

      temp = bondsData.filter((bond) =>
        bond.bondcounsel_userinfo.length === 0 || bond.aag_userinfo.length === 0
      );
    }

    switch (target) {
      case "all":

        break;
      // case "my_transcripts":
      //   temp = bondsData.filter((bond) => bond.aag_userinfo.EmailID?.toString().toUpperCase() === user_email?.toString().toUpperCase()
      //     || bond.legal_assistant_userinfo.EmailID?.toString().toUpperCase() === user_email?.toString().toUpperCase());
      //   break;
      case "in_progress":
        temp = bondsData.filter((bond) =>
          bond.bond_status !== BOND_STATUS_LABEL.RegistrationComplete &&
          bond.bond_status !== BOND_STATUS_LABEL.Cancelled
        );
        break;
      case "completed":

        try {
          let bonds_respo = await getBondsDataService(
            user_email,
            "R"
          );

          bonds_respo = bonds_respo.map((bond) => {

            let formatted_date = "";
            let submission_date = "";

            if (bond.closing_date) {

              let parsedDate = moment(bond.closing_date);

              if (parsedDate.isValid()) {
                formatted_date = parsedDate.format("MM/DD/YYYY");
              }

            }

            if (bond.submission_date) {

              let parsedDate = moment(bond.submission_date);

              if (parsedDate.isValid()) {
                submission_date = parsedDate.format("MM/DD/YYYY");
              }

            }

            return {
              ...bond,
              closing_date: formatted_date,
              submission_date: submission_date
            };

          });

          // setBondsData(bonds_respo);
          setFilteredData(bonds_respo);
          setCurrentPage(1);
          setError("");

          return; // stop execution so bottom setFilteredData doesn't override

        } catch (error) {

          console.error(error);
          setError("Error fetching Bonds");

        }

        break;
        
      case "unassigned":
        temp = bondsData.filter(bondData =>
          bondData.aag_userinfo.length === 0 || bondData.aag_userinfo.some(user => !user.EmailID)
        );

        break;
      default:
        // Handle the default case if needed
        break;
    }

    setFilteredData(temp);
  };

  if (!isLoggedIn) {
    return navigate("/");
  }

  const statusIcons = {
    "New": <FontAwesomeIcon className="status-icon green" icon={faPlusCircle}></FontAwesomeIcon>,
    "Registered": <FontAwesomeIcon className="status-icon emerald-green" icon={faCheckCircle}></FontAwesomeIcon>,
    "Delayed": <FontAwesomeIcon className="status-icon amber" icon={faClock}></FontAwesomeIcon>,
    "Cancelled": <FontAwesomeIcon className="status-icon red" icon={faTimesCircle}></FontAwesomeIcon>,
    "Opinion Issued": <FontAwesomeIcon className="status-icon indigo" icon={faShieldAlt}></FontAwesomeIcon>,
    "Under Review": <FontAwesomeIcon className="status-icon blue" icon={faEye}></FontAwesomeIcon>

  }

  return (
    <div className="dashboard-wrapper">
      <div className="col-md-2">
        <div
          className="side-box"
        >
          <button
            className={`side-nav-btn btn btn-link mb-2 ${filterBy === 'all' ? "active" : ''}`}
            onClick={() => onChangeFilter("all")}
          >
            <FontAwesomeIcon className="status-icon" icon={faFolder}></FontAwesomeIcon> All
          </button>
          <button
            className={`side-nav-btn btn btn-link mb-2 ${filterBy === 'my_transcripts' ? "active" : ''}`}
            onClick={() => onChangeFilter("my_transcripts")}
          >
            <FontAwesomeIcon className="status-icon" icon={faNoteSticky} ></FontAwesomeIcon> My Transcripts
          </button>
          <button
            className={`side-nav-btn btn btn-link mb-2 ${filterBy === 'in_progress' ? "active" : ''}`}
            onClick={() => onChangeFilter("in_progress")}
          >
            <FontAwesomeIcon className="status-icon" icon={faHourglassHalf} ></FontAwesomeIcon> In Progress
          </button>
          <button
            className={`side-nav-btn btn btn-link mb-2 ${filterBy === 'completed' ? "active" : ''}`}
            onClick={() => onChangeFilter("completed")}
          >
            <FontAwesomeIcon className="status-icon" icon={faCheckCircle} ></FontAwesomeIcon> Registered
          </button>
        </div>
      </div>
      <div className="col-md-10">
        <div>
          <div className="col-12">
            <div className="dashboard-header">
              <h1>Bond Counsel Landing Page</h1>
            </div>
            {error && (
              <div className="alert alert-warning">
                <strong>Warning!</strong> {error}
              </div>
            )}
          </div>
        </div>
        <div className="table-container">
          <div className="child-div">
            <div style={{ width: "95%" }}>
              <div className="row search-print-container">
                <div className="col-md-3">
                  <InputGroup>
                    <InputGroup.Text>
                      <FontAwesomeIcon icon={faSearch}></FontAwesomeIcon>
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search"
                      value={searchTerm}
                      onChange={handleSearch}
                    />
                  </InputGroup>
                </div>
                <div className="col-md-6"></div>
                <div className="col-md-3 export-container">
                  <Button
                    className="btn-download"
                    variant="primary"
                    onClick={(event) => {
                      event.stopPropagation();
                      exportTableDataAsCSV(
                        fullHiddenBondsDaTableRef,
                        "BCU"
                      );
                    }}
                  >
                    <FontAwesomeIcon icon={faFileDownload} /> Download
                  </Button>
                  <Button
                    variant="primary"
                    onClick={(event) => {
                      event.stopPropagation();
                      exportTableAsPDF(fullHiddenBondsDaTableRef, "BCU");
                    }}
                  >
                    <FontAwesomeIcon icon={faPrint} /> Print
                  </Button>
                  {/* <Dropdown className="nav-item">
                    <span className="d-inline-block d-sm-none">
                      <Dropdown.Toggle as="a" className="nav-link">
                        <Settings size={18} className="align-middle" />
                      </Dropdown.Toggle>
                    </span>
                    <span className="d-none d-sm-inline-block">
                      <Dropdown.Toggle as="a" className="nav-link">
                        <span className="text-dark">
                          <FontAwesomeIcon icon={faFileDownload} />
                        </span>
                      </Dropdown.Toggle>
                    </span>
                    <Dropdown.Menu drop="end">
                      <Dropdown.Item>
                        <span
                          className="text-dark"
                          onClick={(event) => {
                            event.stopPropagation();
                            exportTableAsPDF(fullHiddenBondsDaTableRef, "BCU");
                          }}
                        >
                          <FontAwesomeIcon icon={faFilePdf} />
                          &nbsp; PDF
                        </span>
                      </Dropdown.Item>
                      <Dropdown.Item>
                        <span
                          className="text-dark"
                          onClick={(event) => {
                            event.stopPropagation();
                            exportTableDataAsCSV(
                              fullHiddenBondsDaTableRef,
                              "BCU"
                            );
                          }}
                        >
                          <FontAwesomeIcon icon={faFileCsv} />
                          &nbsp; CSV
                        </span>
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown> */}
                </div>
              </div>
              <Table
                ref={bondsDaTableRef}
              >
                <thead>
                  <tr style={{ whiteSpace: "nowrap", fontSize: "0.9em" }}>
                    <th onClick={() => handleSorting("bond_issue_number")}>
                      Issue/Opinion #{" "}
                      <span>
                        <FontAwesomeIcon icon={faSort} className="ms-2" />
                      </span>
                    </th>
                    <th onClick={() => handleSorting("closing_date")}>
                      Closing Date
                      <span>
                        <FontAwesomeIcon icon={faSort} className="ms-2" />
                      </span>
                    </th>
                    <th onClick={() => handleSorting("Issuer")}>
                      Issuer{" "}
                      <span>
                        <FontAwesomeIcon icon={faSort} className="ms-2" />
                      </span>
                    </th>
                    <th onClick={() => handleSorting("title")}>
                      Title{" "}
                      <span>
                        <FontAwesomeIcon icon={faSort} className="ms-2" />
                      </span>
                    </th>
                    <th onClick={() => handleSorting("par_dollar_amount")}>
                      Par Dollar Amount{" "}
                      <span>
                        <FontAwesomeIcon icon={faSort} className="ms-2" />
                      </span>
                    </th>
                    <th onClick={() => handleSorting("bond_status")}>
                      Status
                      <span>
                        <FontAwesomeIcon icon={faSort} className="ms-2" />
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentData.map((bond) => (
                    <tr key={bond.id}>
                      <td>
                        {bond.bond_issue_number && (
                          <a
                            href={`/bond-details/${bond.bond_issue_number}/edit`}
                            target="_self"
                            rel="noopener noreferrer"
                          >
                            {bond.bond_issue_number}
                          </a>
                        )}
                      </td>
                      <td>{bond.closing_date}</td>
                      <td>{bond.Issuer}</td>
                      <td>{bond.title}</td>
                      <td>{USDollar.format(bond.par_dollar_amount)}</td>
                      <td className="no-resize">{statusIcons[bond.bond_status]} {bond.bond_status}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* this table is hidden  */}
              <div style={{ display: "none" }}>
                <Table
                  responsive
                  bordered
                  striped
                  hover
                  size="sm"
                  ref={fullHiddenBondsDaTableRef}
                >
                  <thead>
                    <tr style={{ whiteSpace: "nowrap", fontSize: "0.9em" }}>
                      <th>
                        Issue/Opinion #{" "}
                        <span>
                          <FontAwesomeIcon icon={faSort} className="ms-2" />
                        </span>
                      </th>
                      <th>
                        Closing Date{" "}
                        <span>
                          <FontAwesomeIcon icon={faSort} className="ms-2" />
                        </span>
                      </th>
                      <th>
                        Issuer{" "}
                        <span>
                          <FontAwesomeIcon icon={faSort} className="ms-2" />
                        </span>
                      </th>
                      <th>
                        Title{" "}
                        <span>
                          <FontAwesomeIcon icon={faSort} className="ms-2" />
                        </span>
                      </th>
                      <th>
                        Par Dollar Amount{" "}
                        <span>
                          <FontAwesomeIcon icon={faSort} className="ms-2" />
                        </span>
                      </th>
                      <th>
                        Status
                        <span>
                          <FontAwesomeIcon icon={faSort} className="ms-2" />
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((bond) => (
                      <tr key={bond.id}>
                        <td>
                          {bond.bond_issue_number && (
                            <a
                              href={`/bond-details/${bond.bond_issue_number}/edit`}
                              target="_self"
                              rel="noopener noreferrer"
                            >
                              {bond.bond_issue_number}
                            </a>
                          )}
                        </td>
                        <td>{bond.closing_date}</td>
                        <td>{bond.Issuer}</td>
                        <td>{bond.title}</td>
                        <td>{USDollar.format(bond.par_dollar_amount)}</td>
                        <td>{bond.bond_status}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              {/* ------------------------------- */}
              <div className="d-flex justify-content-between">
                <NavLink
                  to={"/upload-transcript"}
                  className={"btn btn-primary me-2 mb-2"}
                >
                  Upload New Transcript
                </NavLink>
                <Pagination className="float-end">
                  <Pagination.First
                    onClick={() => paginate(1)}
                    disabled={currentPage === 1}
                  />
                  <Pagination.Prev
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                  />
                  <Pagination.Item>
                    Page {currentPage} of{" "}
                    {Math.ceil(filteredData.length / itemsPerPage)}
                  </Pagination.Item>
                  <Pagination.Next
                    onClick={() => paginate(currentPage + 1)}
                    disabled={
                      currentPage ===
                      Math.ceil(filteredData.length / itemsPerPage)
                    }
                  />
                  <Pagination.Last
                    onClick={() =>
                      paginate(Math.ceil(filteredData.length / itemsPerPage))
                    }
                    disabled={
                      currentPage ===
                      Math.ceil(filteredData.length / itemsPerPage)
                    }
                  />
                </Pagination>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div >
  );
};

export default BondCounselUserLandingPage;
